import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ClosedLostReportTotals } from '../../types';
import { formatCurrency } from 'utils';

interface Props {
  totals: ClosedLostReportTotals;
}

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

// Large totals are compacted to K/M suffixes to keep the KPI card readable.
const formatCompactDollar = (value: number) =>
  Math.abs(value) >= 100_000 ? compactCurrencyFormatter.format(value) : formatCurrency(value, true);

export const ClosedLostKpis = ({ totals }: Props) => {
  const { totalLostDeals, totalValueLost, grossProfitLost, avgDaysToLost, avgGrossProfitLost, avgTotalValueLost } =
    totals;

  const kpis = [
    { label: 'Total deals lost', value: totalLostDeals.toLocaleString() },
    {
      label: 'Value lost',
      value: formatCompactDollar(totalValueLost),
      sub: `${formatCompactDollar(avgTotalValueLost)} avg deal value`,
    },
    {
      label: 'Gross profit lost',
      value: formatCompactDollar(grossProfitLost),
      sub: `${formatCompactDollar(avgGrossProfitLost)} avg gross / deal`,
      color: 'error.main',
    },
    { label: 'Avg days to lost', value: `${Math.round(avgDaysToLost)}d`, sub: 'from created to lost' },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 1.5,
        mb: 2,
      }}
    >
      {kpis.map((kpi, i) => (
        <Card key={i} variant="outlined">
          <CardContent sx={{ p: '16px 18px !important' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.4}
              display="block"
              mb={0.75}
            >
              {kpi.label}
            </Typography>
            <Typography variant="h5" fontWeight={700} letterSpacing={-0.5} color={kpi.color}>
              {kpi.value}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
              {kpi.sub}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
